import React, { useEffect, useRef, memo, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaSearch, FaTimes } from "react-icons/fa";

interface SearchInputProps {
 initialValue?: string;
 onSearchReady: (searchQuery: string) => void;
 debounceMs?: number;
 placeholder?: string;
 instanceId?: string; // Stable ID for state preservation
}

// Global refs to preserve state across remounts
const globalInputState = new Map<string, {
 value: string;
 wasFocused: boolean;
 cursorPosition: number | null;
}>();

export const SearchInput = memo<SearchInputProps>(({
 initialValue = "",
 onSearchReady,
 debounceMs = 500,
 placeholder,
 instanceId: providedInstanceId,
}) => {
 const { t } = useTranslation();
 const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
 const inputRef = useRef<HTMLInputElement>(null);
 const onSearchReadyRef = useRef(onSearchReady);
 // Use provided instanceId or generate a stable one based on placeholder
 const instanceIdRef = useRef<string>(providedInstanceId || `search-input-${placeholder || "default"}`);
 const containerRef = useRef<HTMLDivElement>(null);

 // Keep callback ref in sync
 useEffect(() => {
  onSearchReadyRef.current = onSearchReady;
 }, [onSearchReady]);

 // Restore or initialize state - use both layout and regular effect to ensure it works
 useLayoutEffect(() => {
  const instanceId = instanceIdRef.current;
  const state = globalInputState.get(instanceId);
  const input = inputRef.current;
  
  if (!input) return;

  // Use requestAnimationFrame to ensure DOM is fully ready
  requestAnimationFrame(() => {
   if (!inputRef.current) return;
   
   if (state) {
    // Restore previous state
    inputRef.current.value = state.value;
    if (state.wasFocused) {
     inputRef.current.focus();
     if (state.cursorPosition !== null) {
      inputRef.current.setSelectionRange(state.cursorPosition, state.cursorPosition);
     }
    }
   } else if (initialValue) {
    // Initialize with initialValue
    inputRef.current.value = initialValue;
    globalInputState.set(instanceId, {
     value: initialValue,
     wasFocused: false,
     cursorPosition: null,
    });
   }
  });
 }, [initialValue]);

 // Also restore on regular effect as backup
 useEffect(() => {
  const instanceId = instanceIdRef.current;
  const state = globalInputState.get(instanceId);
  const input = inputRef.current;
  
  if (!input || !state) return;

  // Restore value if it was lost
  if (input.value !== state.value) {
   const wasFocused = document.activeElement === input;
   input.value = state.value;
   if (state.wasFocused && !wasFocused) {
    input.focus();
    if (state.cursorPosition !== null) {
     input.setSelectionRange(state.cursorPosition, state.cursorPosition);
    }
   }
  }
 });

 // Save state before unmount
 useEffect(() => {
  const instanceId = instanceIdRef.current;
  const input = inputRef.current;
  return () => {
   if (input) {
    globalInputState.set(instanceId, {
     value: input.value,
     wasFocused: document.activeElement === input,
     cursorPosition: input.selectionStart,
    });
   }
  };
 }, []);

 const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const value = e.target.value;
  const input = e.target;
  
  // Save state
  const instanceId = instanceIdRef.current;
  globalInputState.set(instanceId, {
   value,
   wasFocused: true,
   cursorPosition: input.selectionStart,
  });
  
  // Clear existing timer
  if (debounceTimerRef.current) {
   clearTimeout(debounceTimerRef.current);
  }

  // Set new timer
  debounceTimerRef.current = setTimeout(() => {
   onSearchReadyRef.current(value);
  }, debounceMs);
 };

 const handleFocus = () => {
  const instanceId = instanceIdRef.current;
  const input = inputRef.current;
  if (input) {
   globalInputState.set(instanceId, {
    value: input.value,
    wasFocused: true,
    cursorPosition: input.selectionStart,
   });
  }
 };

 const handleBlur = () => {
  setTimeout(() => {
   const instanceId = instanceIdRef.current;
   const input = inputRef.current;
   if (input && document.activeElement !== input) {
    globalInputState.set(instanceId, {
     value: input.value,
     wasFocused: false,
     cursorPosition: null,
    });
   }
  }, 0);
 };

 const handleClear = () => {
  const input = inputRef.current;
  if (input) {
   input.value = "";
   const instanceId = instanceIdRef.current;
   globalInputState.set(instanceId, {
    value: "",
    wasFocused: true,
    cursorPosition: 0,
   });
   if (debounceTimerRef.current) {
    clearTimeout(debounceTimerRef.current);
   }
   onSearchReadyRef.current("");
   input.focus();
  }
 };

 const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
  e.preventDefault();
  e.stopPropagation();
  const input = inputRef.current;
  if (input) {
   const value = input.value;
   if (debounceTimerRef.current) {
    clearTimeout(debounceTimerRef.current);
   }
   onSearchReadyRef.current(value);
  }
 };

 const currentValue = inputRef.current?.value || "";

 return (
  <div ref={containerRef} className="flex-1">
   <form onSubmit={handleSubmit} noValidate>
    <div className="relative">
     <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-store-primary-light text-sm" />
     <input
      ref={inputRef}
      type="text"
      placeholder={placeholder || t("products.searchPlaceholder")}
      defaultValue={initialValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={(e) => {
       if (e.key === "Enter") {
        e.preventDefault();
       }
      }}
      className="w-full pl-10 pr-4 py-2 border border-store-accent-light focus:outline-none focus:ring-2 focus:ring-store-secondary focus:border-store-secondary text-sm"
     />
     {currentValue && (
      <button
       type="button"
       onClick={handleClear}
       className="absolute right-3 top-1/2 transform -translate-y-1/2 text-store-primary-light hover:text-store-primary"
      >
       <FaTimes className="text-sm" />
      </button>
     )}
    </div>
   </form>
  </div>
 );
}, (prevProps, nextProps) => {
 // STRICT comparison - only rerender if placeholder or debounceMs changes
 // Completely ignore onSearchReady and initialValue to prevent ANY rerenders
 // The component manages its own state, so it doesn't need to rerender
 // when these props change
 return (
  prevProps.placeholder === nextProps.placeholder &&
  prevProps.debounceMs === nextProps.debounceMs
  // Intentionally ignoring initialValue and onSearchReady
  // initialValue is handled via useEffect
  // onSearchReady is handled via ref
 );
});

SearchInput.displayName = "SearchInput";


